import type {
  Address,
  CreateAddressRequest,
  CreateSiteRequest,
  Organisation,
  UpdateAddressRequest,
  UpdateOrganisationRequest,
} from '@my/directory-api';

/**
 * Form shapes and mappers for the organisation panel.
 *
 * <p>
 * DELIBERATELY A COPY of the equivalents in
 * `apps/tms/src/features/settings/organisation/lib/formMappers.ts`, trimmed to
 * the fields My Hub shows. Copied rather than imported because an app may not
 * reach into another app's internals — but it IS duplication, and the two will
 * drift. The real fix is lifting the organisation-identity block into a shared
 * package once it has settled in both places.
 * </p>
 */

export interface AddressForm {
  code: string;
  fullAddress: string;
  city: string;
  postcode: string;
  countryIsoCode: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string;
  /**
   * Where the coordinates came from: `Google` (picked / Find on map) or
   * `Manual` (pin dragged or tapped). Forwarded on save so Directory never
   * refreshes a hand-placed pin back off the gate.
   */
  coordinatesSource: 'Google' | 'Manual' | null;
}

export const EMPTY_ADDRESS_FORM: AddressForm = {
  code: '',
  fullAddress: '',
  city: '',
  postcode: '',
  countryIsoCode: 'IE',
  latitude: null,
  longitude: null,
  placeId: '',
  coordinatesSource: null,
};

export interface OrganisationForm {
  name: string;
  invoicingName: string;
  taxNumber: string;
  registrationNumber: string;
  operationalTimeZone: string;
  languageCode: string;
  timePattern: string;
  datePattern: string;
}

export function toOrganisationForm(o: Organisation): OrganisationForm {
  return {
    name: o.name ?? '',
    invoicingName: o.invoicingName ?? '',
    taxNumber: o.taxNumber ?? '',
    registrationNumber: o.registrationNumber ?? '',
    operationalTimeZone: o.operationalTimeZone ?? '',
    languageCode: o.languageCode ?? '',
    timePattern: o.timePattern ?? '',
    datePattern: o.datePattern ?? '',
  };
}

export function toAddressForm(address: Address): AddressForm {
  return {
    code: address.code ?? '',
    fullAddress: address.fullAddress ?? '',
    city: address.city ?? '',
    postcode: address.postcode ?? '',
    countryIsoCode: address.countryIsoCode ?? '',
    latitude: address.latitude ?? null,
    longitude: address.longitude ?? null,
    placeId: address.placeId ?? '',
    coordinatesSource: address.coordinatesSource ?? null,
  };
}

/**
 * Build the PUT body for `/organisations`.
 *
 * <p>
 * WHOLE-OBJECT REPLACE. The endpoint defaults every field it is not given, so
 * the fields this panel does not show — accounting system, VAT rounding,
 * payment terms, extra fields — are echoed back from the loaded record. Omit
 * them and saving a name would quietly reset a customer's finance settings.
 * </p>
 */
export function toUpdateOrganisationRequest(
  org: Organisation,
  form: OrganisationForm,
  mainOfficeAddressId: string | null,
  /**
   * The main office's country, which is where the country is actually edited —
   * this panel has no country field of its own, exactly as TMS does not.
   *
   * <p>
   * It matters because the organisation's own `countryIsoCode` drives the tax
   * schedule and nominal-code defaults. Blank keeps the current value rather
   * than blanking a required column: an address cleared mid-edit must not wipe
   * the country.
   * </p>
   */
  mainOfficeCountryIsoCode?: string | null,
): UpdateOrganisationRequest {
  const countryIsoCode =
    (mainOfficeCountryIsoCode ?? '').trim().toUpperCase() || org.countryIsoCode;

  return {
    organisationId: org.id,
    name: form.name.trim(),
    invoicingName: form.invoicingName.trim(),
    taxNumber: form.taxNumber.trim() || null,
    registrationNumber: form.registrationNumber.trim() || null,

    operationalTimeZone: form.operationalTimeZone,
    languageCode: form.languageCode,
    timePattern: form.timePattern,
    datePattern: form.datePattern,
    countryIsoCode,

    // ── Not edited here, echoed so the replace does not default them ──────
    // The base currency is read-only in My Hub: moving it reshuffles
    // additional currencies and bank accounts, which is a finance job and
    // lives in the app that owns those screens.
    currencyIsoCode: org.currencyIsoCode,
    additionalCurrencyIsoCodes: org.additionalCurrencyIsoCodes,
    extraFields: org.extraFields,
    accountingSystem: org.accountingSystem,
    vatRoundingMethod: org.vatRoundingMethod,
    defaultPaymentTermDays: org.defaultPaymentTermDays,
    defaultPaymentTermBasis: org.defaultPaymentTermBasis,

    mainOfficeAddressId,
  };
}

export function toCreateAddressRequest(
  organisationId: string,
  form: AddressForm,
): CreateAddressRequest {
  return {
    organisationId,
    addressType: 'Primary',
    name: 'Main Office',
    code: form.code.trim() || null,
    fullAddress: form.fullAddress.trim(),
    city: form.city.trim() || null,
    postcode: form.postcode.trim() || null,
    countryIsoCode: form.countryIsoCode.trim().toUpperCase(),
    latitude: form.latitude,
    longitude: form.longitude,
    coordinatesSource: form.coordinatesSource ?? null,
    placeId: form.placeId.trim(),
    status: 'Active',
  };
}

/**
 * Every Address in the directory belongs to exactly one Site, so creating the
 * main office without a paired Site leaves orphan data nothing else can point
 * at — a vehicle's home base, a party's location. Mirrors TMS.
 */
export function toCreateSiteRequest(organisationId: string, addressId: string): CreateSiteRequest {
  return {
    organisationId,
    name: 'Main Office',
    type: 'Site',
    addressId,
    status: 'Active',
  };
}

export function toUpdateAddressRequest(address: Address, form: AddressForm): UpdateAddressRequest {
  return {
    addressId: address.id,
    addressType: address.addressType,
    name: address.name,
    code: form.code.trim() || null,
    fullAddress: form.fullAddress.trim(),
    city: form.city.trim() || null,
    postcode: form.postcode.trim() || null,
    countryIsoCode: form.countryIsoCode.trim().toUpperCase(),
    latitude: form.latitude,
    longitude: form.longitude,
    coordinatesSource: form.coordinatesSource ?? null,
    placeId: form.placeId.trim(),
    status: address.status,
    serviceTags: address.serviceTags ?? null,
  };
}
